import { makeStyles, styled } from "@mui/styles";
import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Tab,
  TableHead,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDownward, ArrowUpward, Refresh } from "@mui/icons-material";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getRefreshTime } from "../../../utils/getRefreshTime";
import TablePaginationActions from "../../table/TablePaginationActions";
import { getFarmer } from "../../../redux/reducer/farmer/farmerReducer";
import TableDropwdownActions from "../../table/TableDropwdownActions";
import {
  debtMoneyActions,
  deleteDebtMoney,
  getDebtMoney,
  sortDebtMoney,
  updateDebtMoney,
} from "../../../redux/reducer/debtMoney/debtMoneyReducer";

const useStyles = makeStyles(() => ({
  headerClassSearch: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    padding: "0px 5px",
    margin: "10px 0px",
    background: "#F2F2F2",
  },
  tableHead: {
    backgroundColor: "#f5f2e6",
  },
  actionButtonDelete: {
    backgroundColor: "#dde0f0",
    color: "#A0A3B3",
    fontSize: "25px",
    borderRadius: "5px",
    marginRight: "10px",
    cursor: "pointer",
    "&:hover": {
      background: "#ba2941",
      color: "white",
    },
  },
  actionButtonEdit: {
    backgroundColor: "#dde0f0",
    color: "#A0A3B3",
    fontSize: "25px",
    borderRadius: "5px",
    marginRight: "10px",
    cursor: "pointer",
    "&:hover": {
      background: "#7dbda9",
      color: "white",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#499178",
    color: theme.palette.common.white,
    border: "none!important",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "#f0faf6",
    cursor: "pointer",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#f0faf6",
    },
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function FarmerDebtMoney() {
  // Navigate
  const navigate = useNavigate();

  // Styling
  const styles = useStyles();

  //Redux
  const data = useSelector((state) => state.debtMoney.data);
  const token = useSelector((state) => state.auth.token);
  const getTime = useSelector((state) => state.debtMoney.updatedTime);
  const role = useSelector((state) => state.auth.dataToken.roleId.roleName);
  const farmer = useSelector((state) => state.farmer.data);
  const dispatch = useDispatch();

  // Local State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formInput, setFormInput] = useState({
    to: "",
    from: "",
    limit: 0,
  });
  const [sortingField, setSortingField] = useState([]);
  const [searchField, setSearchField] = useState([]);
  const [fetch, setFetch] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [timeUpdated, setTimeUpdated] = useState({
    hour: 0,
    minute: 0,
  });
  const [column, setColumn] = useState([
    {
      field: "Tanggal Dibuat",
      sort: "asc",
      value: "debtDetails.submissionDate",
    },
    { field: "Petani", sort: "asc", value: "farmerId" },
    { field: "Total Hutang" },
    { field: "Sisa Hutang" },
    { field: "Tenor" },
    { field: "Status Hutang" },
    { field: "Aksi" },
  ]);
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleSort = (data) => {
    let x = column.findIndex((item) => item.field === data);
    if (x > -1) {
      let newColumn = [...column];
      if (column[x].sort === "asc") {
        newColumn[x] = {
          ...newColumn[x],
          sort: "desc",
        };
      } else {
        newColumn[x] = {
          ...newColumn[x],
          sort: "asc",
        };
      }
      setColumn(newColumn);
    }
    let sortData = {
      token: token,
      body: {
        isActive: true,
      },
      params: {
        page: 1,
        limit: 10,
        paginate: true,
        sort:
          column[x].sort === "asc" ? column[x].value : `-${column[x].value}`,
      },
    };
    dispatch(sortDebtMoney(sortData)).then(() => {
      // toast.success(res.payload.message);
    });
  };

  useEffect(() => {
    if (fetch) {
      handleAddSearch();
      setFetch(false);
    }
    if (getTime > 0) {
      setTimeUpdated(getRefreshTime(getTime));
    }
  }, [fetch, getTime]);

  // Handle Search
  const handleChangeSearch = (index) => (e) => {
    const newItems = [...searchField];
    newItems[index][e.target.name] = e.target.value;
    setSearchField(newItems);
  };

  const handleAddSearch = () => {
    setSearchField((prevItems) => [
      ...prevItems,
      {
        value: "",
        field: "",
      },
    ]);
  };

  const handleChangeInput = (e) => {
    setFormInput({ ...formInput, [e.target.name]: e.target.value });
  };

  const handleChangePage = (event, newPage) => {
    if (event) {
      setPage(newPage);
      const getData = {
        params: {
          page: newPage + 1,
          limit: 10,
        },
        token: token,
      };
      dispatch(getDebtMoney(getData));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin menghapus permanent hutang petani ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const deleteData = {
          data: id,
          token: token,
        };
        dispatch(deleteDebtMoney(deleteData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(debtMoneyActions.deleteDebtMoney(id));
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const deleteClassItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin menghapus hutang petani ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const deleteData = {
          value: {
            isActive: false,
          },
          id: id,
          token: token,
        };
        dispatch(updateDebtMoney(deleteData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(debtMoneyActions.editDebtMoney(id));
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const activeClassItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin mengaktifkan hutang petani ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aktifkan",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const activatedData = {
          value: {
            isActive: true,
          },
          id: id,
          token: token,
        };
        dispatch(updateDebtMoney(activatedData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(
              getDebtMoney({
                params: {
                  params: {},
                },
                token: token,
              })
            );
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const checkingFilter = (pageValue) => {
    let sort = [];
    sortingField.map((item) => {
      if (item.sorting === "asc") {
        sort.push(`${item.field}`);
      } else {
        sort.push(`-${item.field}`);
      }
      return "";
    });

    let body = {};
    for (let i = 0; i < searchField.length; i++) {
      if (searchField[i].value !== "" && searchField[i].field === "status") {
        body[searchField[i].field] = {
          $regex: searchField[i].value,
          $options: "i",
        };
      } else if (searchField[i].field === "farmerId") {
        body[searchField[i].field] = searchField[i].value;
      }
    }

    if (formInput.from) {
      if (formInput.to) {
        body = {
          ...body,
          createdAt: {
            $gte: moment(formInput.from).format(),
            $lte: moment(formInput.to).format(),
          },
        };
      } else {
        body = {
          ...body,
          createdAt: {
            $gte: moment(formInput.from).format(),
            $lte: moment(formInput.to)
              .add(23, "hours")
              .add(59, "minutes")
              .format(),
          },
        };
      }
    }

    let filterData = {
      token: token,
      body: {
        ...body,
        isActive: isActive,
      },
      params: {
        page: pageValue,
        limit: formInput.limit > 0 ? formInput.limit : 10,
        paginate: true,
        sort: sort.toString().replaceAll(",", " "),
      },
    };
    return filterData;
  };

  const handleSubmit = () => {
    dispatch(sortDebtMoney(checkingFilter(1))).then((res) => {
      toast.success(res.payload.message);
    });
  };

  const handleReset = () => {
    setSortingField([]);
    setIsActive(true);
    setSearchField([]);
    handleAddSearch();
    setFormInput({
      to: "",
      from: "",
      limit: 0,
    });
  };

  const handleUpdate = () => {
    const getData = {
      params: {
        params: {},
      },
      token: token,
    };
    dispatch(getDebtMoney(getData));
  };

  const handleEditData = (id) => {
    navigate("/petani/hutang/edit/" + id);
  };

  const loadMore = (value) => (event) => {
    if (
      event.target.scrollHeight - event.target.scrollTop < 300 &&
      event.target.scrollTop < 100
    ) {
      if (farmer.totalDocs !== farmer.docs.length && value === 1) {
        const getData = {
          params: {
            limit: farmer.limit + 10,
            page: 1,
          },
          token: token,
        };
        dispatch(getFarmer(getData));
      }
    }
  };

  const statusData = [
    {
      value: 1,
      label: "Belum Bayar",
    },
    {
      value: 2,
      label: "Sudah Bayar",
    },
  ];

  return (
    <>
      <ToastContainer />
      <Card className={styles.headerClassSearch}>
        <Grid padding={0.5} container spacing={0.5} flexDirection={"column"}>
          <Grid
            item
            container
            xs={12}
            sm={12}
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <div
              style={{ display: "flex", alignItems: "center", width: "10%" }}
            >
              <Typography variant="h6" color="primary">
                PENCARIAN
              </Typography>
            </div>
            <div style={{ display: "flex", width: "50%" }}></div>
          </Grid>
          <Divider style={{ width: "100%", border: "1px solid black" }} />
          <Grid
            item
            container
            xs={12}
            sm={12}
            direction="column"
            justifyContent="space-evenly"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <FormControl
                    variant="standard"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      flexDirection: "row",
                      width: "60%",
                      minWidth: "60%",
                      margin: "5px",
                    }}
                  >
                    <TextField
                      size="small"
                      name="field"
                      inputProps={{ readOnly: true }}
                      value="Tanggal Pencarian"
                      variant="outlined"
                      style={{ marginRight: "10px", width: "32.5%" }}
                    />

                    <div style={{ width: "65%" }}>
                      <FormControl
                        variant="standard"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexDirection: "column",
                          width: "80%",
                        }}
                      >
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          label="Tanggal Mulai"
                          name="from"
                          size="small"
                          type="date"
                          onChange={handleChangeInput}
                          value={formInput.from}
                          variant="outlined"
                          style={{ width: "49%", marginBottom: "10px" }}
                        />
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          label="Tanggal Akhir"
                          name="to"
                          size="small"
                          type="date"
                          onChange={handleChangeInput}
                          value={formInput.to}
                          variant="outlined"
                          style={{ width: "49%" }}
                        />
                      </FormControl>
                    </div>
                  </FormControl>
                </div>
                {searchField.map((item, index) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                      key={index}
                    >
                      <FormControl
                        variant="standard"
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          flexDirection: "row",
                          width: "65%",
                          minWidth: "65%",
                          margin: "5px",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          name="field"
                          id={index}
                          onChange={handleChangeSearch(index)}
                          defaultValue="purchaseOrderCode"
                          value={item.field}
                          variant="outlined"
                          style={{ marginRight: "10px", width: "30%" }}
                        >
                          <MenuItem value="status">Status</MenuItem>
                          <MenuItem value="farmerId">Petani</MenuItem>
                        </TextField>

                        {item.field !== "purchaseOrderCode" &&
                        item.field !== "totalPrices" ? (
                          <TextField
                            select
                            size="small"
                            error={false}
                            name="value"
                            required
                            onChange={handleChangeSearch(index)}
                            value={item.value}
                            variant="outlined"
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  onScroll: loadMore(1),
                                },
                                style: {
                                  height: "300px",
                                },
                              },
                              displayEmpty: true,
                            }}
                            style={{
                              width: "100%",
                              maxWidth: "400px",
                              marginRight: "10px",
                            }}
                          >
                            {item.field === "farmerId" && farmer.docs.length > 0
                              ? farmer.docs.map((data, index) => (
                                  <MenuItem value={data._id} key={index}>
                                    {data.fullName}
                                  </MenuItem>
                                ))
                              : item.field === "farmerId" && (
                                  <MenuItem value="">Data kosong</MenuItem>
                                )}
                            {item.field === "status" &&
                              statusData.map((item, index) => {
                                return (
                                  <MenuItem value={item.value} key={index}>
                                    {item.label}
                                  </MenuItem>
                                );
                              })}
                          </TextField>
                        ) : (
                          <TextField
                            id={index}
                            type="text"
                            size="small"
                            placeholder="Masukan kata kunci"
                            name="value"
                            onChange={handleChangeSearch(index)}
                            defaultValue="asc"
                            value={item.value}
                            variant="outlined"
                            style={{ width: "65%" }}
                          />
                        )}
                      </FormControl>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          marginLeft: "-80px",
                        }}
                      >
                        <FormControl style={{ margin: "10px", width: "20%" }}>
                          <TextField
                            onChange={handleChangeInput}
                            InputLabelProps={{ shrink: true }}
                            type="number"
                            size="small"
                            label="Limit"
                            name="limit"
                            value={formInput.limit}
                            fullWidth
                            variant="outlined"
                            style={{ width: "100%", maxWidth: "450px" }}
                          />
                        </FormControl>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Grid>
          <Grid
            container
            item
            justifyContent="flex-end"
            xs={12}
            sm={12}
            marginBottom={1}
          >
            <Button
              variant="contained"
              size="small"
              style={{ margin: "0px 5px" }}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              style={{ margin: "0px 10px" }}
              onClick={() => handleReset()}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card
        style={{
          padding: "10px",
          background: "#F2F2F2",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "10px 5px",
          }}
        >
          <div>
            {role === "super-admin" ||
            role === "supervisor" ||
            role === "admin" ? (
              <Button
                variant="contained"
                onClick={() => navigate("/petani/hutang/tambah")}
              >
                Tambah Hutang Petani
              </Button>
            ) : (
              ""
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Refresh
              className={styles.actionButtonEdit}
              onClick={() => handleUpdate()}
            />
            <i>
              updated {timeUpdated.hour > 0 ? `${timeUpdated.hour} hour` : ""}{" "}
              {`${timeUpdated.minute} minutes ago`}
            </i>
          </div>
        </div>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "#ddd",
            marginBottom: "10px",
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant="fullWidth"
            aria-label="basic tabs example"
          >
            <Tab label="Summary" value={0} />
            <Tab label="Hutang Petani" value={1} />
          </Tabs>
        </Box>
        {value === 0 && (
          <Box
            width={"100%"}
            height="500px"
            display={"flex"}
            justifyContent="center"
            gap="20px"
            padding="20px"
          >
            <Box
              width="300px"
              height="125px"
              border="1px solid black"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              Data Summary 1
            </Box>
            <Box
              width="300px"
              height="125px"
              border="1px solid black"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              Data Summary 1
            </Box>
            <Box
              width="300px"
              height="125px"
              border="1px solid black"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              Data Summary 1
            </Box>
          </Box>
        )}
        {value === 1 && (
          <Paper
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "column",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead className={styles.classTableHead}>
                  <StyledTableRow>
                    {column.length > 0 &&
                      column.map((item, index) => (
                        <StyledTableCell
                          align="center"
                          key={index}
                          onClick={() => handleSort(item.field)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {item.field}{" "}
                            {item.sort && (
                              <div>
                                {item.sort === "asc" ? (
                                  <ArrowUpward />
                                ) : (
                                  <ArrowDownward />
                                )}
                              </div>
                            )}
                          </Box>
                        </StyledTableCell>
                      ))}
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {data.docs.length > 0 ? (
                    data.docs.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.debtDetails
                            ? moment(row.debtDetails.submissionDate).format(
                                "DD - MMM - YYYY"
                              )
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.farmerId ? row.farmerId.fullName : "-"}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.debtDetails
                            ? row.debtDetails.amount.toLocaleString("id", {
                                style: "currency",
                                currency: "IDR",
                              })
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.debtDetails
                            ? row.debtDetails.remainingAmount.toLocaleString(
                                "id",
                                {
                                  style: "currency",
                                  currency: "IDR",
                                }
                              )
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.debtDetails ? row.debtDetails.tenor : "-"}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 160 }} align="center">
                          {row.status === 1 ? (
                            <Button
                              style={{ background: "#7da175", color: "white" }}
                            >
                              Hutang
                            </Button>
                          ) : (
                            <Button
                              style={{ background: "#385931", color: "white" }}
                            >
                              Lunas
                            </Button>
                          )}
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 170 }} align="center">
                          <TableDropwdownActions
                            handleEditData={() => handleEditData(row._id)}
                            handleDetailData={() =>
                              navigate(`/petani/hutang/detail/${row._id}`)
                            }
                            deleteClassItem={() => deleteClassItem(row._id)}
                            deleteItem={() => deleteItem(row._id)}
                            activeClassItem={() => activeClassItem(row._id)}
                            styles={styles}
                            row={row}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell style={{ width: "100%" }} align="center">
                        Tidak ada data
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {data.docs.length > 0 ? (
              <TablePagination
                rowsPerPageOptions={""}
                className={styles.pagination}
                rowsPerPage={rowsPerPage}
                colSpan={12}
                count={data.totalDocs}
                page={page}
                SelectProps={{
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                }}
                sx={{ width: "100%" }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            ) : (
              ""
            )}
          </Paper>
        )}
      </Card>
    </>
  );
}
